import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, Observable, catchError, concatMap, from, map, of, pipe, switchMap, tap } from 'rxjs';
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from '@application/data-access';
import { IGroupedPermissions, IRolePermission, IUserRole, IVerifyEmail } from './entities/role.interface';
import { IUser } from './entities/user.interface';
import { PasswordDefinitionMode } from './entities/user-profile.interface';
import { UserService } from './infra/user.service';
import { ErrorStore } from '@application/errors/data-access';
import { AlertStore } from '@alert/data-access';
import { HttpErrorResponse } from '@angular/common/http';
import { tapResponse } from '@ngrx/operators';

import { DxOdataStoreService } from '@shared/components/dx-data-grid/data-access';
import DataSource from 'devextreme/data/data_source';
import { APP_CONFIG } from '@application/config';
import { TLoginCode } from './entities/user.interface';
import { PermissionNumber, SelfUserStore } from '@person/user/self/data-access';
import { IPerson } from '@person/register/data-access';

const TEMP_AUTH_PERMISSION_GUARD_PATH = 'TEMPORARY_AUTH_MODAL.PERMISSION_GUARD';

export interface IUsersStore {
  currentRole: IUserRole;
  userData: IUser;
  currentUserId: string;
  roleTemplate: IRolePermission[];
  alreadyVerify: Record<string, IVerifyEmail>;
  loginCode: TLoginCode,
  myAccount: IPerson;
}

export const initialUserStore: IUsersStore = {
  currentRole: null,
  userData: null,
  currentUserId: null,
  roleTemplate: null,
  alreadyVerify: {},
  loginCode: null,
  myAccount: null
};

export const UserStore = signalStore(
  { providedIn: 'root' },
  withState(initialUserStore),
  withMethods((
    state,
    userService = inject(UserService),
    alertStore = inject(AlertStore),
    errorStore = inject(ErrorStore),
    url = inject(APP_CONFIG).apiUrl,
    appSource = inject(APP_CONFIG).appSource,
    dxOdataStoreService = inject(DxOdataStoreService),
    selfUserStore = inject(SelfUserStore),
  ) => {
    const ensureRolePermissions = (
      permissions: PermissionNumber[],
      translationKey: string,
    ) =>
      from(
        selfUserStore.ensurePermissions(permissions, {
          title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.${translationKey}.TITLE`,
          message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.${translationKey}.MESSAGE`,
        }),
      );

    const guardRolePermission = <T>(
      requestKey: string,
      permissions: PermissionNumber[],
      translationKey: string,
      work: () => Observable<T>,
    ) =>
      ensureRolePermissions(permissions, translationKey).pipe(
        switchMap((hasPermission) => {
          if (!hasPermission) {
            patchState(state, setPropInit(requestKey));
            return EMPTY;
          }

          return work();
        }),
      );

    return {
      getUsers(): DataSource<unknown, any> {
        return dxOdataStoreService.fromOdata({
          url: url.odata_person_service,
          metadata: 'Users',
          key: 'PersonId',
          params: {
            $select: 'PersonId,FullName,FirstName,LastName,Email,IsActive,Role,IsOwner',
            $filter: 'IsOwner eq false'
          }
        });
      },
      getRoles(): DataSource<unknown, any> {
        return dxOdataStoreService.fromOdata<any>({
          url: url.odata_user_service,
          metadata: 'Roles',
          key: 'RoleId',
          permissionGuard: {
            permissions: [PermissionNumber.FuncoesObterTodos],
            translationKey: 'ROLE_LIST',
          },
        });
      },
      loadRoleTemplate: rxMethod<void>(
        pipe(
          tap(() => patchState(state, { ...setPropLoading('loadRoleTemplate') })),
          switchMap(() => userService.getRoleTemplate().pipe(
            tapResponse({
              next: (roleTemplate) => {
                patchState(state, { roleTemplate, ...setPropLoaded('loadRoleTemplate') });
              },
              error: (error) => {
                patchState(state, { ...setPropError('loadRoleTemplate', error) });
              }
            })
          )),
        )
      ),
      loadRole: rxMethod<string>(
        pipe(
          tap(() => patchState(state, { ...setPropLoading('loadRole') })),
          switchMap((roleId) =>
            guardRolePermission(
              'loadRole',
              [PermissionNumber.FuncoesObterPorId],
              'ROLE_VIEW',
              () =>
                userService.getRole(roleId).pipe(
                  tapResponse({
                    next: (role) => {
                      patchState(state, { currentRole: role, ...setPropLoaded('loadRole') });
                    },
                    error: (error) => {
                      patchState(state, { ...setPropError('loadRole', error) });
                    },
                  }),
                ),
            ),
          ),
        ),
      ),
      createRole: rxMethod<IUserRole>(
        pipe(
          tap(() => patchState(state, { ...setPropLoading('createRole') })),
          switchMap((role) =>
            guardRolePermission(
              'createRole',
              [PermissionNumber.FuncoesAdicionar],
              'ROLE_CREATE',
              () =>
                userService.createRole(role).pipe(
                  tapResponse({
                    next: (createdRole) => {
                      patchState(state, { currentRole: createdRole, ...setPropLoaded('createRole') });
                      alertStore.genericSuccess();
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(state, { ...setPropError('createRole', error) });
                    },
                  }),
                ),
            ),
          ),
        ),
      ),
      updateRole: rxMethod<IUserRole>(
        pipe(
          tap(() => patchState(state, { ...setPropLoading('updateRole') })),
          switchMap((role) =>
            guardRolePermission(
              'updateRole',
              [PermissionNumber.FuncoesAtualizar],
              'ROLE_UPDATE',
              () =>
                userService.updateRole(role).pipe(
                  tapResponse({
                    next: (updatedRole) => {
                      patchState(state, { currentRole: updatedRole, ...setPropLoaded('updateRole') });
                      alertStore.genericSuccess();
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(state, { ...setPropError('updateRole', error) });
                    },
                  }),
                ),
            ),
          ),
        ),
      ),
      removeRole: rxMethod<string>(
        pipe(
          tap(() => patchState(state, { ...setPropLoading('removeRole') })),
          switchMap((roleId) =>
            guardRolePermission(
              'removeRole',
              [PermissionNumber.FuncoesRemover],
              'ROLE_DELETE',
              () =>
                userService.deleteRole(roleId).pipe(
                  tapResponse({
                    next: (role) => {
                      patchState(state, { currentRole: role, ...setPropLoaded('removeRole') });
                      alertStore.genericSuccess();
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(state, { ...setPropError('removeRole', error) });
                    },
                  }),
                ),
            ),
          ),
        ),
      ),
      loadUser: rxMethod<string>(pipe(
        tap(() => patchState(state, { ...setPropLoading('loadUser') })),
        switchMap((userId) => userService.getUser(userId).pipe(
          tapResponse({
            next: (user) => {
              patchState(state, { userData: user, ...setPropLoaded('loadUser') });
            },
            error: (error) => {
              patchState(state, { ...setPropError('loadUser', error) });
            }
          })
        )),
      )),
      removeUser: rxMethod<string>(pipe(
        tap(() => patchState(state, { ...setPropLoading('removeUser') })),
        switchMap((userId) => userService.deleteUser(userId).pipe(
          tapResponse({
            next: (user) => {
              patchState(state, { userData: user, ...setPropLoaded('removeUser') });
              alertStore.genericSuccess();
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(state, { ...setPropError('removeUser', error) });
            }
          })
        )),
      )),
      /**
       * Creates or updates a user using POST request
       * Always uses POST (addUser) for both creating and updating users/clients
       * @param user - User data to create or update
       * @param isCreatingNewPerson - Optional flag (kept for backward compatibility, no longer affects behavior)
       * @param skipUpdate - Optional flag (kept for backward compatibility, no longer affects behavior)
       */
      createOrUpdateUser: rxMethod<{ user: IUser; isCreatingNewPerson?: boolean; skipUpdate?: boolean }>(pipe(
        tap(() => patchState(state, { ...setPropLoading('addUser') })),
        switchMap(({ user }) => {
          const userWithAppSource = { 
            ...user, 
            AppSource: user.AppSource ?? appSource,
            PasswordDefinitionMode: user.PasswordDefinitionMode ?? PasswordDefinitionMode.None
          };
          const email = user.Email;

          if (!email) {
            patchState(state, { ...setPropError('addUser', new HttpErrorResponse({ error: 'Email is required' })) });
            return EMPTY;
          }

          // Always use POST for both create and update operations
          return userService.addUser(userWithAppSource).pipe(
            tapResponse({
              next: (createdOrUpdatedUser) => {
                patchState(state, { userData: createdOrUpdatedUser, ...setPropLoaded('addUser') });
                alertStore.genericSuccess();
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(state, { ...setPropError('addUser', error) });
              }
            })
          );
        })
      )),
      generateLoginCode: rxMethod<void>(pipe(
        tap(() => patchState(state, { ...setPropLoading('generateLoginCode') })),
        switchMap(() => userService.generateLoginCode().pipe(
          tapResponse({
            next: (loginCode) => {
              patchState(state, { loginCode, ...setPropLoaded('generateLoginCode') });
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(state, { ...setPropError('generateLoginCode', error) });
            }
          })
        )),
      )),
      verifyEmail: rxMethod<string>(pipe(
        concatMap((email) => userService.verifyEmail(email).pipe(
          tap((response) => {
            patchState(state, { alreadyVerify: { ...state.alreadyVerify(), [email]: response } });
          }),
          catchError((error) => {
            errorStore.setHttpError(error);
            return of(error);
          })
        )),
      )),
      getMyAccount: rxMethod<string>(pipe(
        tap(() => patchState(state, { ...setPropLoading('getMyAccount') })),
        switchMap((payload) => userService.getMyAccount(payload).pipe(
          tapResponse({
            next: (myAccount) => {
              patchState(state, { myAccount, ...setPropLoaded('getMyAccount') });
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(state, { ...setPropError('getMyAccount', error) });
            }
          })
        )),
      )),
      updateMyAccount: rxMethod<Partial<IPerson>>(pipe(
        tap(() => patchState(state, { ...setPropLoading('updateMyAccount') })),
        switchMap((payload) => userService.updateMyAccount(payload).pipe(
          tapResponse({
            next: (myAccount) => {
              patchState(state, { myAccount, ...setPropLoaded('updateMyAccount') });
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(state, { ...setPropError('updateMyAccount', error) });
            }
          })
        )),
      )),
      groupPermissionsByModule: (
        permissions: IRolePermission[]
      ): IGroupedPermissions[] => {
        const reducedPermissions = permissions.reduce((group: any, permission) => {
          const { Group } = permission;
          group[Group] = group[Group] ?? [];
          group[Group].push(permission);
          return group;
        }, {});
        const localArray = [];
        for (const [group, value] of Object.entries(reducedPermissions)) {
          const options = value as IRolePermission[];
          const isEveryOptionChecked = options.every((option) => option.Checked);
          const localObject = {
            group,
            opened: false,
            options: options,
            isEveryOptionChecked,
          };
          localArray.push(localObject);
        }
        const sortedArr = localArray.sort((a, b) => {
          if (a.group < b.group) return -1;
          if (a.group > b.group) return 1;
          return 0;
        });
        return structuredClone(sortedArr);
      },
      setPropsInit(props: string[]): void {
        patchState(state, setPropInit(...props));
      },
      blankState: () => patchState(state, initialUserStore),
      blankRoleState: () => patchState(state, { currentRole: null }),
      blankRequestState: () => {
        setPropInit(
          'loadRoleTemplate',
          'loadRole',
          'loadUser',
          'createRole',
          'updateRole',
          'removeRole',
          'updateUser',
          'removeUser',
          'addUser'
        );
      },
    }
  }),
  withReqState(
    'loadRoleTemplate',
    'loadRole',
    'loadUser',
    'createRole',
    'updateRole',
    'removeRole',
    'updateUser',
    'removeUser',
    'addUser',
    'generateLoginCode',
    'getMyAccount',
    'updateMyAccount'
  )
);
